import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AppProviders } from "./AppProviders";
import { LoginModal, ProtectedRoute } from "../features/auth";
import { Spinner } from "../shared/components/ui";
import NotFoundPage from "../shared/components/NotFoundPage";
import NotificationsInit from "../features/notifications/components/NotificationsInit";
import { AppBootGate } from "../features/system";

// Eager: landing + core catalog (first paint, and they use vendor scripts).
import { HomePage } from "../features/home";
import { CategoryPage, ServicesPage, ServiceDetailPage } from "../features/services";

// Lazy: everything else, code-split with a skeleton fallback.
const ContactPage         = lazy(() => import("../features/contact").then(m => ({ default: m.ContactPage })));
const AboutPage           = lazy(() => import("../features/about").then(m => ({ default: m.AboutPage })));
const ReviewsPage         = lazy(() => import("../features/reviews").then(m => ({ default: m.ReviewsPage })));
const BlogListPage        = lazy(() => import("../features/blog").then(m => ({ default: m.BlogListPage })));
const BlogDetailPage      = lazy(() => import("../features/blog").then(m => ({ default: m.BlogDetailPage })));
const JoinProfessionalPage= lazy(() => import("../features/professional").then(m => ({ default: m.JoinProfessionalPage })));
const CorporateEnquiryPage= lazy(() => import("../features/corporate").then(m => ({ default: m.CorporateEnquiryPage })));
const ProfilePage         = lazy(() => import("../features/auth").then(m => ({ default: m.ProfilePage })));
const CartPage            = lazy(() => import("../features/cart").then(m => ({ default: m.CartPage })));
const CheckoutPage        = lazy(() => import("../features/checkout").then(m => ({ default: m.CheckoutPage })));
const BookingSuccessPage  = lazy(() => import("../features/checkout").then(m => ({ default: m.BookingSuccessPage })));
const BookingsListPage    = lazy(() => import("../features/bookings").then(m => ({ default: m.BookingsListPage })));
const BookingDetailPage   = lazy(() => import("../features/bookings").then(m => ({ default: m.BookingDetailPage })));
const AddressesPage       = lazy(() => import("../features/address").then(m => ({ default: m.AddressesPage })));
const SearchPage          = lazy(() => import("../features/search").then(m => ({ default: m.SearchPage })));
const DealsPage           = lazy(() => import("../features/deals").then(m => ({ default: m.DealsPage })));
const SupportPage         = lazy(() => import("../features/support").then(m => ({ default: m.SupportPage })));

const loadScript = (src, type) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = false;
    if (type) script.type = type;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

const RouteFallback = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Spinner size={36} color="#082189" />
  </div>
);

// Resets scroll to top on navigation and recalculates GSAP ScrollTrigger so
// stale triggers from the previous route don't cause jumpy scrolling.
const RouteScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const st = window.ScrollTrigger;
    if (st && typeof st.refresh === "function") {
      const id = setTimeout(() => { try { st.refresh(); } catch (e) { /* noop */ } }, 80);
      return () => clearTimeout(id);
    }
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    if (window.citycraftScriptsLoaded) return;
    window.citycraftScriptsLoaded = true;

    const loadAllScripts = async () => {
      const scripts = [
        { src: process.env.PUBLIC_URL + "/assets/js/vendor/jquery.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/bootstrap-bundle.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/swiper-bundle.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/plugin.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/three.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/slick.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/scroll-magic.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/hover-effect.umd.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/magnific-popup.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/parallax-slider.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/nice-select.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/purecounter.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/isotope-pkgd.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/imagesloaded-pkgd.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/ajax-form.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/Observer.min.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/splitting.min.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/webgl.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/parallax-scroll.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/atropos.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/slider-active.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/main.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/tp-cursor.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/portfolio-slider-1.js" },
        { src: process.env.PUBLIC_URL + "/assets/js/distortion-img.js", type: "module" },
        { src: process.env.PUBLIC_URL + "/assets/js/skew-slider/index.js", type: "module" },
        { src: process.env.PUBLIC_URL + "/assets/js/img-revel/index.js", type: "module" },
      ];

      for (const s of scripts) {
        try {
          await loadScript(s.src, s.type);
        } catch (e) {
          console.error("Failed to load script", s.src);
        }
      }
    };
    loadAllScripts();
  }, []);

  return (
    <BrowserRouter>
      <AppBootGate>
      <AppProviders>
        <RouteScrollManager />
        <NotificationsInit />
        <LoginModal />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/join-professional" element={<JoinProfessionalPage />} />
            <Route path="/corporate-enquiry" element={<CorporateEnquiryPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/support" element={<SupportPage />} />

            {/* Booking journey */}
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
            <Route path="/booking/success/:bookingID" element={<ProtectedRoute><BookingSuccessPage /></ProtectedRoute>} />

            {/* Account (protected) */}
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/profile/addresses" element={<ProtectedRoute><AddressesPage /></ProtectedRoute>} />
            <Route path="/bookings" element={<ProtectedRoute><BookingsListPage /></ProtectedRoute>} />
            <Route path="/bookings/:bookingID" element={<ProtectedRoute><BookingDetailPage /></ProtectedRoute>} />

            {/* Catalog (dynamic — keep last so static routes win) */}
            <Route path="/:category" element={<CategoryPage />} />
            <Route path="/:category/:subCategoryID" element={<ServicesPage />} />
            <Route path="/:category/:subCategoryID/:serviceID" element={<ServiceDetailPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AppProviders>
      </AppBootGate>
    </BrowserRouter>
  );
}

export default App;
